import React, { useEffect } from 'react'
import useBreakpoint from 'hooks/useBreakPoint'
import { Typography } from 'component_library/1_Atoms/Typography'
import { Button } from '@/base_components/ui/button'
import { Input } from '@/base_components/ui/input'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { wpFormPath } from '../constants'
import { useLeadGenContext, ForwardingFilters } from 'context/LeadGen'
import { getPersistedParams } from 'lib/marketingParams'
import classNames from 'classnames'
import './index.scss'
import { getParsedQuery } from 'utils/common'
import { stateAbbreviations } from 'components/app/SeFriendly/validateUsaState'

interface InputProps {
  className: string
  value: string
  onChange: (string) => void
}

interface ButtonProps {
  className?: string
  onClick?: (query?: ForwardingFilters) => void
  disabled?: boolean
}

interface LinkProps {
  className?: string
  forwardingFilters?: ForwardingFilters
}

export const InputElement: React.FC<InputProps> = ({
  className,
  value,
  onChange,
}) => {
  return (
    <Input
      type="email"
      placeholder="Email address"
      className={classNames(
        '~rounded-full ~px-6 ~pr-[60px] focus-visible:~ring-0 focus-visible:~outline-[#DADADA] focus-visible:~outline-offset-0 leadGenInput',
        className
      )}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

export const ButtonElement: React.FC<ButtonProps> = ({
  className,
  disabled,
  onClick,
}) => {
  return (
    <Button
      variantType="v2"
      disabled={disabled}
      className={classNames([
        '~bg-navy ~text-white disabled:~opacity-100 disabled:~bg-navy-50 ~h-[50px] hover:~bg-navy ',
        className,
      ])}
      onClick={() => onClick()}
    >
      Continue
    </Button>
  )
}

export const LinkElement: React.FC<LinkProps> = ({
  className,
  children,
  forwardingFilters = {},
}) => {
  const classes = classNames('~text-link', className)
  const query = { ...getPersistedParams(), ...forwardingFilters }

  return (
    <Link
      className={classes}
      href={{
        pathname: wpFormPath,
        query,
      }}
    >
      {children}
    </Link>
  )
}

const LeadGenerationElements: React.FC<{
  isEmptySearch?: boolean
  isWaterfallTreatment?: boolean
  hasLowAvailabilitySpacing?: boolean
}> = ({ isEmptySearch, isWaterfallTreatment, hasLowAvailabilitySpacing }) => {
  const { isMobileScreen } = useBreakpoint()
  const router = useRouter()
  const {
    inputValue,
    isValidEmailCheck,
    onChangeInput,
    onClickButton,
  } = useLeadGenContext()
  const { state } = getParsedQuery(router)
  const fullState = stateAbbreviations[state as string]
  const subTitleText = isWaterfallTreatment
    ? `Our other providers in ${fullState} are currently helping others, but schedules change daily. We'll reach out as soon as a spot opens up.`
    : "We don't have any openings right now, but schedules change daily. We'll reach out as soon as a spot opens up."

  useEffect(() => {
    router.prefetch(wpFormPath)
  }, [])

  const classes = classNames('~bg-latte-50', {
    '~mt-20 emptySearchContainer ~flex ~flex-col ~items-center ~bg-[#F7F5EE]': isEmptySearch,
    empty_container_height: isEmptySearch,
    // Always separate "Stay updated" from the search results in low-availability
    // (no waterfall control above it), matching the 200px/44px gap the waterfall
    // controls provide. Padding (not margin) so it sits inside the latte-50
    // background instead of leaving a white gap.
    '~pt-[44px] md:~pt-[200px]': hasLowAvailabilitySpacing && !isEmptySearch,
  })

  return (
    <div className={classes}>
      <section className="~flex ~flex-col ~flex-1 ~items-center ~justify-center ~text-center pageElements ~px-5">
        <Typography
          className="leadGenHeader"
          text="Stay updated on availability"
          tag="h2"
          size={isMobileScreen ? '3xl' : '4xl'}
          weight="bold"
        />
        <Typography
          text={subTitleText}
          tag="p"
          size="lg"
          family="Gibson"
          weight="normal"
          className="~max-w-[515px] emptySearchText"
        />
        <div className="~flex ~gap-0 ~mt-6">
          <InputElement
            value={inputValue}
            onChange={onChangeInput}
            className={classNames(
              '~rounded-full ~px-6 ~pr-[60px] focus-visible:~ring-0 focus-visible:~ring-offset-0 focus-visible:~outline-offset-0 ~z-[1] ~border-[#DADADA] ~border-1 leadGenInput',
              {
                ['~max-w-[349px]']: isMobileScreen,
                ['~min-w-[349px]']: !isMobileScreen,
              },
              '~z-[1]'
            )}
          />
          <ButtonElement
            disabled={Boolean(inputValue) && !isValidEmailCheck}
            onClick={onClickButton}
            className="~ml-[-60px] ~z-[2]"
          />
        </div>
      </section>
      <img
        src={
          isMobileScreen ? '/images/emptybgmobile.svg' : '/images/emptybg.svg'
        }
        alt="background for empty search"
        width="100%"
        className="~mt-10 lg:~mt-[-50px]"
      />
    </div>
  )
}

export default LeadGenerationElements
